import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import CustomerDiscoveryNav from '../../components/navigation/CustomerDiscoveryNav';
import { useUserLocation } from '../../contexts/UserLocationContext';
import FilterPanel from './components/FilterPanel';
import PhotographerGrid from './components/PhotographerGrid';
import { photographerService } from '../../services/photographerService';
import { calculateDistance } from '../../services/locationService';
import {
  fetchPortfolioCoverMap,
  getCardCoverUrl,
  getCardAvatarUrl,
  getPhotographerUserId,
} from '../../services/portfolioCoverService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BackButton from '../../components/BackButton';

const BrowsePhotographers = () => {
  console.log('🔵 BrowsePhotographers component mounted');
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    city: 'closest',
    photographyType: 'all',
    experienceLevel: 'all',
    minPrice: 0,
    maxPrice: 10000,
    sortBy: 'rating'
  });

  const [photographers, setPhotographers] = useState([]);
  const [filteredPhotographers, setFilteredPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { userLocation } = useUserLocation();

  // Map city names to values for filtering
  const cityMap = {
    'الرياض': 'riyadh',
    'جدة': 'jeddah',
    'الدمام': 'dammam',
    'المدينة المنورة': 'medina',
    'مكة المكرمة': 'mecca',
    'الطائف': 'taif',
    'أبها': 'abha',
    'خميس مشيط': 'khamees_mushait',
    'جازان': 'jazan',
    'نجران': 'najran',
    'الباحة': 'baha',
    'تبوك': 'tabuk',
    'حائل': 'hail',
    'بريدة': 'buraydah',
    'سكاكا': 'sakaka',
  };

  // Map experience level — DB field takes priority over years calculation
  const getExperienceLevel = (dbLevel, years) => {
    const DB_LEVEL_MAP = {
      certified:     { label: 'معتمد',  value: 'certified' },
      distinguished: { label: 'مميز',   value: 'distinguished' },
      elite:         { label: 'نخبة',   value: 'elite' },
      // legacy enum values
      beginner:      { label: 'معتمد',  value: 'certified' },
      intermediate:  { label: 'مميز',   value: 'distinguished' },
      advanced:      { label: 'مميز',   value: 'distinguished' },
      expert:        { label: 'نخبة',   value: 'elite' },
    };
    if (dbLevel && DB_LEVEL_MAP[dbLevel]) return DB_LEVEL_MAP[dbLevel];
    // Fallback: derive from years
    const y = years || 0;
    if (y >= 8) return { label: 'نخبة',  value: 'elite' };
    if (y >= 4) return { label: 'مميز',  value: 'distinguished' };
    return { label: 'معتمد', value: 'certified' };
  };

  // Convert database photographer to card format (covers supplied via batch map)
  const formatPhotographerForCard = (photographer, userLoc, coverMap = new Map()) => {
    let portfolioImage = null;
    let portfolioImageAlt = 'صورة من معرض المصور';
    let portfolioImageOriginal = null;

    const photographerId = getPhotographerUserId(photographer);
    const cover = photographerId ? coverMap.get(photographerId) : null;
    if (cover) {
      portfolioImageOriginal = cover.imageUrl;
      portfolioImage = getCardCoverUrl(cover.imageUrl);
      portfolioImageAlt = cover.alt;
    }

    const experienceLevel = getExperienceLevel(
      photographer.experienceLevel || photographer.experience_level || null,
      photographer.experienceYears || photographer.experience_years || 0
    );
    const city = photographer.city || 'الرياض';
    const cityValue = cityMap[city] || city.toLowerCase().replace(/\s+/g, '_');

    // Map specializations to photography types for filtering
    // Hidden: 'حفلات الزفاف', 'التصوير الشخصي', 'تصوير المناسبات', 'التصوير التجاري'
    const specializationToTypeMap = {
      'تصوير المنتجات': 'product',
      'تصوير الطعام': 'food',
      'تصوير العقارات': 'real-estate',
      'تصوير الطبيعة': 'nature',
      'التصوير المعماري': 'architectural',
      'تصوير الأطفال': 'children',
      'التصوير الصحفي': 'journalistic',
    };

    const photographyTypes = (photographer.specializations || []).map(spec => 
      specializationToTypeMap[spec] || spec.toLowerCase().replace(/\s+/g, '-')
    );

    // Filter out hidden specialties from display
    const hiddenSpecialties = [
      'حفلات الزفاف',
      'التصوير الشخصي',
      'تصوير المناسبات',
      'التصوير التجاري',
      'تصوير الأزياء',
      'التصوير الرياضي',
    ];
    const filteredSpecialties = (photographer.specializations || []).filter(
      spec => !hiddenSpecialties.includes(spec)
    );

    // Calculate distance if user location is available
    let distanceKm = null;
    const serviceCenterLat = photographer.serviceCenterLat || photographer.service_center_lat;
    const serviceCenterLng = photographer.serviceCenterLng || photographer.service_center_lng;
    
    if (userLoc?.lat && userLoc?.lng && serviceCenterLat && serviceCenterLng) {
      distanceKm = calculateDistance(
        userLoc.lat,
        userLoc.lng,
        parseFloat(serviceCenterLat),
        parseFloat(serviceCenterLng)
      );
      distanceKm = Math.round(distanceKm * 10) / 10;
    }

    const rawAvatar =
      photographer.user?.avatarUrl ||
      photographer.user?.avatar_url ||
      'https://img.rocket.new/generatedImages/rocket_gen_img_1161a7ebd-1763294739154.png';

    return {
      id: photographerId,
      name: photographer.user?.fullName || photographer.user?.full_name || 'مصور',
      city: city,
      avatar: getCardAvatarUrl(rawAvatar),
      avatarOriginal: rawAvatar,
      avatarAlt: `صورة ${photographer.user?.fullName || photographer.user?.full_name || 'المصور'}`,
      portfolioImage,
      portfolioImageOriginal,
      portfolioImageAlt,
      specialties: filteredSpecialties, // Use filtered specialties instead of all
      experienceLevel: experienceLevel.label,
      rating: parseFloat(photographer.rating) || 0,
      reviewCount: photographer.totalReviews || photographer.total_reviews || 0,
      startingPrice: 0, // Dynamic pricing - no fixed price
      isVerified: photographer.status === 'active' || photographer.availableForBooking || photographer.available_for_booking,
      photographyTypes: photographyTypes,
      cityValue: cityValue,
      experienceLevelValue: experienceLevel.value,
      distanceKm: distanceKm, // null if location not available
      shootingMethods: Array.isArray(photographer.shootingMethods ?? photographer.shooting_methods) ? (photographer.shootingMethods || photographer.shooting_methods) : []
    };
  };

  // Load photographers from database
  useEffect(() => {
    const loadPhotographers = async () => {
      try {
        setLoading(true);
        setError('');

        console.log('📍 Loading photographers with userLocation:', userLocation);
        const photographersData = await photographerService.getAllPhotographers();
        
        if (!photographersData || photographersData.length === 0) {
          console.warn('No photographers found in database');
          setPhotographers([]);
          setFilteredPhotographers([]);
          setLoading(false);
          return;
        }

        console.log(`Found ${photographersData.length} photographers`);

        const photographerIds = photographersData
          .map((p) => getPhotographerUserId(p))
          .filter(Boolean);

        const formattedWithoutCovers = photographersData.map((p) =>
          formatPhotographerForCard(p, userLocation, new Map()),
        );
        setPhotographers(formattedWithoutCovers);
        setFilteredPhotographers(formattedWithoutCovers);
        setLoading(false);

        const coverMap = await fetchPortfolioCoverMap(photographerIds);
        const formattedPhotographers = photographersData.map((p) =>
          formatPhotographerForCard(p, userLocation, coverMap),
        );

        setPhotographers(formattedPhotographers);
        setFilteredPhotographers(formattedPhotographers);
      } catch (err) {
        console.error('Failed to load photographers:', err);
        setError('فشل تحميل قائمة المصورين. يرجى المحاولة مرة أخرى.');
        setPhotographers([]);
        setFilteredPhotographers([]);
      } finally {
        setLoading(false);
      }
    };

    loadPhotographers();
  }, [userLocation]); // Reload when user location becomes available

  useEffect(() => {
    let filtered = [...photographers];

    if (filters?.city === 'closest') {
      // لا فلتر حسب المدينة؛ الترتيب حسب المسافة يتم أدناه
    } else if (filters?.city !== 'all') {
      filtered = filtered?.filter((p) => p?.cityValue === filters?.city);
    }

    if (filters?.photographyType !== 'all') {
      filtered = filtered?.filter((p) => p?.photographyTypes?.includes(filters?.photographyType));
    }

    if (filters?.experienceLevel !== 'all') {
      filtered = filtered?.filter((p) => p?.experienceLevelValue === filters?.experienceLevel);
    }

    filtered = filtered?.filter((p) =>
    p?.startingPrice >= filters?.minPrice && p?.startingPrice <= filters?.maxPrice
    );

    if (filters?.city === 'closest' && userLocation?.lat != null && userLocation?.lng != null) {
      // ترتيب حسب الأقرب لموقع العميل (المصدر: الموقع في الهيدر)
      filtered?.sort((a, b) => {
        const aKm = a?.distanceKm ?? Infinity;
        const bKm = b?.distanceKm ?? Infinity;
        return aKm - bKm;
      });
    } else {
      switch (filters?.sortBy) {
        case 'rating':
          filtered?.sort((a, b) => b?.rating - a?.rating);
          break;
        case 'price-low':
          filtered?.sort((a, b) => a?.startingPrice - b?.startingPrice);
          break;
        case 'price-high':
          filtered?.sort((a, b) => b?.startingPrice - a?.startingPrice);
          break;
        case 'experience':
          const experienceOrder = { 'elite': 3, 'distinguished': 2, 'certified': 1, 'expert': 3, 'advanced': 2, 'intermediate': 2, 'beginner': 1 };
          filtered?.sort((a, b) => (experienceOrder?.[b?.experienceLevelValue] || 1) - (experienceOrder?.[a?.experienceLevelValue] || 1));
          break;
        default:
          break;
      }
    }

    setFilteredPhotographers(filtered);
  }, [filters, photographers, userLocation]);

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-20 lg:pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-4">
            <BackButton to="/" label="" />
          </div>
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
              تصفح المصورين المحترفين
            </h1>
            <p className="text-base md:text-lg text-muted-foreground">
              اكتشف أفضل المصورين في المملكة العربية السعودية واحجز جلسة التصوير المثالية
            </p>
          </div>

          <CustomerDiscoveryNav />

          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            resultCount={filteredPhotographers?.length} />


          {error && (
            <div className="mt-6 bg-error/10 border border-error/20 rounded-xl p-6 text-center">
              <Icon name="AlertCircle" size={32} className="mx-auto mb-3 text-error" />
              <p className="text-error font-medium mb-2">{error}</p>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                iconName="RefreshCw"
              >
                إعادة المحاولة
              </Button>
            </div>
          )}

          {!loading && !error && filteredPhotographers?.length === 0 && (
            <div className="mt-12 text-center">
              <Icon name="Search" size={64} className="mx-auto mb-4 text-muted-foreground opacity-50" />
              <h2 className="text-2xl font-semibold text-foreground mb-2">لا توجد نتائج</h2>
              <p className="text-muted-foreground mb-4">
                لم يتم العثور على مصورين يطابقون معايير البحث المحددة
              </p>
              <Button
                variant="outline"
                onClick={() => setFilters({
                  city: 'closest',
                  photographyType: 'all',
                  experienceLevel: 'all',
                  minPrice: 0,
                  maxPrice: 10000,
                  sortBy: 'rating'
                })}
                iconName="X"
              >
                إعادة تعيين الفلاتر
              </Button>
            </div>
          )}

          <PhotographerGrid
            photographers={filteredPhotographers}
            loading={loading} />

          {!loading && !error && filteredPhotographers?.length > 0 && (
            <div className="mt-8 text-center text-sm text-muted-foreground">
              عرض {filteredPhotographers?.length} من {photographers?.length} مصور
            </div>
          )}
        </div>
      </main>
    </div>);

};

export default BrowsePhotographers;